/**
 * Seller/advisor bid proposal (RedOaks Bid Instructions) — separate from LP capital packs.
 * Captures the four required sections: effective date, financing/contingencies,
 * validity through Aug 14, and clarification contacts.
 */

export const BID_PROCESS_DEFAULTS = Object.freeze({
  bidsDue: '2026-08-05',
  bidDueTime: '12:00',
  bidDueTz: 'America/Chicago',
  bidDueLabel: 'Wednesday, August 5, 2026 · 12:00pm CST',
  effectiveDate: '2026-08-01',
  proposalValidThrough: '2026-08-14',
  submitTo: Object.freeze({
    name: 'David Carter',
    title: 'Partner',
    firm: 'RedOaks Energy Advisors, LLC',
    email: 'david.carter@redoaksadvisors.com',
    phone: '214-536-8616',
  }),
  cc: Object.freeze([
    Object.freeze({
      name: 'Heather Agnew',
      title: 'Sr. Associate',
      email: 'heather.agnew@redoaksadvisors.com',
    }),
    Object.freeze({
      name: 'Eli Garcia',
      title: 'Analyst II',
      email: 'eli.garcia@redoaksadvisors.com',
    }),
  ]),
  packageName: 'ETXENERGY, LLC — East Texas Operated Acquisition Opportunity',
  formatNote:
    'Written proposal on company letterhead, delivered as an email attachment.',
});

const DEFAULT_CONTINGENCIES = [
  'Customary PSA negotiation and definitive documentation',
  'Midstream contract assignability / gathering ownership transfer confirmation',
];

function cleanStr(v) {
  return String(v ?? '').trim();
}

function cleanContact(c = {}) {
  return {
    name: cleanStr(c.name),
    email: cleanStr(c.email),
    office: cleanStr(c.office),
    mobile: cleanStr(c.mobile),
    role: cleanStr(c.role),
  };
}

/**
 * Build default proposal draft from DealPackage meta + optional prior draft.
 */
export function createBidProposal({ meta = {}, prior = null, bidPriceMM = null } = {}) {
  const process = meta.bidProcess || {};
  const base = prior && typeof prior === 'object' ? prior : {};
  const contacts =
    Array.isArray(base.contacts) && base.contacts.length
      ? base.contacts.map(cleanContact)
      : [{ name: '', email: '', office: '', mobile: '', role: 'Primary' }];

  const contingencies =
    Array.isArray(base.contingencies) && base.contingencies.length
      ? base.contingencies.map(cleanStr).filter(Boolean)
      : [...DEFAULT_CONTINGENCIES];

  return {
    purchasePriceMM:
      base.purchasePriceMM != null && Number.isFinite(+base.purchasePriceMM)
        ? +base.purchasePriceMM
        : bidPriceMM != null && Number.isFinite(+bidPriceMM)
          ? +bidPriceMM
          : null,
    effectiveDate:
      cleanStr(base.effectiveDate) ||
      cleanStr(process.effectiveDate) ||
      cleanStr(meta.effectiveDate) ||
      BID_PROCESS_DEFAULTS.effectiveDate,
    validThrough:
      cleanStr(base.validThrough) ||
      cleanStr(process.proposalValidThrough) ||
      BID_PROCESS_DEFAULTS.proposalValidThrough,
    letterheadEntity:
      cleanStr(base.letterheadEntity) ||
      cleanStr(meta.caParty) ||
      'Firebrand Energy, LLC',
    financingNarrative: cleanStr(base.financingNarrative),
    financingSources: cleanStr(base.financingSources),
    financingStructure: cleanStr(base.financingStructure),
    financingCertainty: cleanStr(base.financingCertainty),
    contingencies,
    contacts,
    submission: {
      letterReviewed: !!base.submission?.letterReviewed,
      letterheadPdfReady: !!base.submission?.letterheadPdfReady,
      emailedToAdvisor: !!base.submission?.emailedToAdvisor,
      submittedAt: cleanStr(base.submission?.submittedAt) || null,
      clarificationNotes: cleanStr(base.submission?.clarificationNotes),
    },
  };
}

/** Compact snapshot embedded in scenario freezes (hashable). */
export function pickProposalSnapshot(proposal) {
  if (!proposal) return null;
  const p = createBidProposal({ prior: proposal, bidPriceMM: proposal.purchasePriceMM });
  return {
    purchasePriceMM: p.purchasePriceMM,
    effectiveDate: p.effectiveDate,
    validThrough: p.validThrough,
    letterheadEntity: p.letterheadEntity,
    financingNarrative: p.financingNarrative,
    financingSources: p.financingSources,
    financingStructure: p.financingStructure,
    financingCertainty: p.financingCertainty,
    contingencies: p.contingencies,
    contacts: p.contacts.map(cleanContact),
    submission: {
      letterReviewed: !!p.submission.letterReviewed,
      letterheadPdfReady: !!p.submission.letterheadPdfReady,
      emailedToAdvisor: !!p.submission.emailedToAdvisor,
      submittedAt: p.submission.submittedAt || null,
      clarificationNotes: p.submission.clarificationNotes || '',
    },
  };
}

/**
 * Validate proposal fields required by RedOaks Bid Instructions.
 * Returns { ok, blockers[] } — blockers are human-readable.
 */
export function validateBidProposal(proposal, { requireSubmissionFlags = false } = {}) {
  const p = createBidProposal({ prior: proposal, bidPriceMM: proposal?.purchasePriceMM });
  const blockers = [];

  if (p.purchasePriceMM == null || !(p.purchasePriceMM > 0)) {
    blockers.push('Purchase price ($MM) is required');
  }
  if (!p.letterheadEntity) blockers.push('Bidding entity / letterhead name is required');
  if (!p.effectiveDate) blockers.push('Effective date is required');
  if (!p.validThrough) blockers.push('Proposal validity date is required');
  const minValid = BID_PROCESS_DEFAULTS.proposalValidThrough;
  if (p.validThrough && p.validThrough < minValid) {
    blockers.push(`Proposal must remain in effect through at least ${minValid}`);
  }
  if (!p.financingNarrative && !p.financingSources) {
    blockers.push('Financing description (sources and/or narrative) is required');
  }
  if (!p.financingCertainty) {
    blockers.push('Financing certainty / availability through closing is required');
  }

  const usableContacts = p.contacts.filter((c) => c.name && c.email && (c.office || c.mobile));
  if (!usableContacts.length) {
    blockers.push(
      'At least one contact with name, email, and office or mobile phone is required',
    );
  }

  if (requireSubmissionFlags) {
    if (!p.submission.letterReviewed) blockers.push('Mark letter as reviewed before send');
    if (!p.submission.letterheadPdfReady) blockers.push('Mark letterhead PDF ready before send');
  }

  return { ok: blockers.length === 0, blockers, proposal: p };
}

/** Plain-text email draft body for the RedOaks submission. */
export function buildBidSubmissionEmailDraft(proposal, { process = BID_PROCESS_DEFAULTS } = {}) {
  const p = createBidProposal({ prior: proposal, bidPriceMM: proposal?.purchasePriceMM });
  const to = process.submitTo || BID_PROCESS_DEFAULTS.submitTo;
  const cc = process.cc || BID_PROCESS_DEFAULTS.cc;
  const price =
    p.purchasePriceMM != null ? `$${Number(p.purchasePriceMM).toFixed(2)}MM` : '[purchase price]';

  return {
    to: to.email,
    cc: cc.map((c) => c.email).join('; '),
    subject: `Bid Proposal — ${process.packageName || BID_PROCESS_DEFAULTS.packageName}`,
    body: [
      `Dear ${to.name},`,
      '',
      `Please find attached ${p.letterheadEntity || '[Entity]'}'s written proposal for the ${process.packageName || BID_PROCESS_DEFAULTS.packageName}.`,
      '',
      `Proposed purchase price: ${price}`,
      `Effective date: ${p.effectiveDate}`,
      `Proposal remains in effect through: ${p.validThrough}`,
      '',
      'Please contact the individuals listed in the attached letter with any questions.',
      '',
      'Regards,',
      p.letterheadEntity || '[Entity]',
    ].join('\n'),
  };
}

/** Merge bid-process facts for diligence / meta surfaces. */
export function bidProcessFromMeta(meta = {}) {
  const bp = meta.bidProcess || {};
  const merged = {
    ...BID_PROCESS_DEFAULTS,
    ...bp,
    submitTo: { ...BID_PROCESS_DEFAULTS.submitTo, ...(bp.submitTo || {}) },
    cc: Array.isArray(bp.cc) && bp.cc.length ? bp.cc : [...BID_PROCESS_DEFAULTS.cc],
    effectiveDate: bp.effectiveDate || meta.effectiveDate || BID_PROCESS_DEFAULTS.effectiveDate,
    bidsDue: bp.bidsDue || meta.bidsDue || BID_PROCESS_DEFAULTS.bidsDue,
  };
  const dealId = meta.dealId || meta.id;
  if (
    dealId &&
    dealId !== 'eagle-etx-2026' &&
    merged.packageName === BID_PROCESS_DEFAULTS.packageName
  ) {
    merged.packageName = meta.package || meta.packageName || dealId;
  }
  return merged;
}
